// NS-908: every CSV/Excel export endpoint now uploads the file to GCS and returns a signed URL to
// fetch it from, instead of streaming the file bytes directly in the response body (cedar-backend
// schemas/export.py's own ExportDownloadOut, added on the ns-908_export_to_gcs branch). This is
// the one shape every migrated endpoint returns - CSV and Excel alike, no per-endpoint variation.
export interface ExportDownloadOut {
  download_url: string;
  expires_at: string;
  generated_at?: string | null;
  file_name: string;
  files: number;
  cache_hit: boolean;
}

// A GCS v4 signed URL is self-contained (the signature lives in its own query string) - no
// Authorization header or bearer token needed to fetch it, so this is a plain browser navigation,
// not another authenticated API call. The bucket-side object already carries a
// `Content-Disposition: attachment; filename="..."` header (export_storage.py's own
// signed_download), so the browser saves it with the right name on its own; the `download`
// attribute here is just a same-origin-download hint as a defensive backup, not load-bearing.
export const triggerExportDownload = (response: ExportDownloadOut): void => {
  const link = document.createElement("a");
  link.href = response.download_url;
  link.download = response.file_name;
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Every export handler across this app used to either skip error-checking entirely (blindly
// handing a failed request's {error, status} object to `new Blob([res])` as if it were real file
// bytes - a corrupted, garbage download) or check for it in a handful of slightly different ways.
// One shared check for the new response shape.
export const isExportDownload = (response: unknown): response is ExportDownloadOut =>
  Boolean(response) && typeof response === "object" && "download_url" in (response as Record<string, unknown>);
